import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const CharacterMatchingQuestion = ({ 
  characters, 
  descriptions, 
  correctMatches, 
  onAnswer,
  isAnswered 
}) => {
  const [matches, setMatches] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  // ── Drag-and-drop (desktop) ──────────────────────────────────────
  const handleDragStart = (characterId) => {
    setDraggedItem(characterId);
  };

  const handleDrop = (descriptionId) => {
    if (!draggedItem || isAnswered) return;
    
    const newMatches = { ...matches };
    Object.keys(newMatches)?.forEach(key => {
      if (newMatches?.[key] === draggedItem) {
        delete newMatches?.[key];
      }
    });
    
    newMatches[descriptionId] = draggedItem;
    setMatches(newMatches);
    setDraggedItem(null);
  };

  // ── Tap-to-select (touch / mobile) ──────────────────────────────
  const handleCharacterTap = (characterId) => {
    if (isAnswered || showFeedback) return;
    setSelectedCharacter(prev => prev === characterId ? null : characterId);
  };

  const handleDescriptionTap = (descriptionId) => {
    if (isAnswered || showFeedback) return;

    if (selectedCharacter) {
      // Assign selected character to this description
      const newMatches = { ...matches };
      // Remove character from any previous slot
      Object.keys(newMatches)?.forEach(key => {
        if (newMatches?.[key] === selectedCharacter) {
          delete newMatches?.[key];
        }
      });
      newMatches[descriptionId] = selectedCharacter;
      setMatches(newMatches);
      setSelectedCharacter(null);
    } else if (matches?.[descriptionId]) {
      // Tap an already-matched description to un-assign
      const newMatches = { ...matches };
      delete newMatches?.[descriptionId];
      setMatches(newMatches);
    }
  };

  const handleSubmit = () => {
    if (Object.keys(matches)?.length !== descriptions?.length) return;
    
    let correctCount = 0;
    descriptions?.forEach(desc => {
      if (matches?.[desc?.id] === correctMatches?.[desc?.id]) {
        correctCount++;
      }
    });

    const isCorrect = correctCount === descriptions?.length;
    setShowFeedback(true);
    
    setTimeout(() => {
      onAnswer(isCorrect);
    }, 2500);
  };

  const getMatchStyle = (descriptionId) => {
    if (!showFeedback) {
      const isTarget = selectedCharacter && !matches?.[descriptionId];
      return matches?.[descriptionId]
        ? 'border-primary bg-primary/5'
        : isTarget
        ? 'border-primary/60 bg-primary/5 border-dashed' :'border-dashed border-muted-foreground/30';
    }

    const isCorrect = matches?.[descriptionId] === correctMatches?.[descriptionId];
    return isCorrect 
      ? 'border-success bg-success/10' :'border-error bg-error/10';
  };

  const getMatchIcon = (descriptionId) => {
    if (!showFeedback) return null;
    
    const isCorrect = matches?.[descriptionId] === correctMatches?.[descriptionId];
    return isCorrect 
      ? <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0" />
      : <Icon name="XCircle" size={20} className="text-error flex-shrink-0" />;
  };

  const isCharacterMatched = (characterId) => {
    return Object.values(matches)?.includes(characterId);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">
          Match Characters to Descriptions
        </h3>
        <p className="text-xs sm:text-sm text-muted-foreground">
          <span className="hidden sm:inline">Drag and drop, or </span>
          <span className="sm:hidden">Tap a character then tap a description. </span>
          <span className="hidden sm:inline">tap</span> a character then tap a description to match.
        </p>
      </div>

      {/* Mobile: stacked layout; Desktop: side-by-side grid */}
      <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:gap-6">
        {/* Characters column */}
        <div className="space-y-2 sm:space-y-3">
          <h4 className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide">
            Characters
          </h4>
          {characters?.map((character) => {
            const matched = isCharacterMatched(character?.id);
            const isSelected = selectedCharacter === character?.id;
            return (
              <div
                key={character?.id}
                draggable={!isAnswered && !showFeedback}
                onDragStart={() => handleDragStart(character?.id)}
                onClick={() => handleCharacterTap(character?.id)}
                className={`rounded-xl border-2 transition-all duration-200 touch-manipulation select-none ${
                  isAnswered || showFeedback
                    ? 'cursor-not-allowed opacity-50 border-primary bg-primary/5'
                    : isSelected
                    ? 'border-secondary bg-secondary/10 shadow-md scale-[1.02] cursor-pointer'
                    : matched
                    ? 'border-primary/40 bg-primary/5 opacity-60 cursor-pointer' :'border-primary bg-primary/5 hover:bg-primary/10 cursor-pointer active:scale-[0.98]'
                }`}
                style={{ minHeight: '56px' }}
              >
                <div className="flex items-center gap-3 px-4 py-3 sm:py-4">
                  <Icon
                    name={isSelected ? 'CheckCircle' : 'GripVertical'}
                    size={20}
                    className={isSelected ? 'text-secondary' : 'text-primary'}
                  />
                  <span className="font-semibold text-foreground text-sm sm:text-base">
                    {character?.name}
                  </span>
                  {matched && !isSelected && (
                    <span className="ml-auto text-xs text-primary/60 font-medium">matched</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Descriptions column */}
        <div className="space-y-2 sm:space-y-3">
          <h4 className="text-xs sm:text-sm font-semibold text-foreground uppercase tracking-wide">
            Descriptions
          </h4>
          {descriptions?.map((description) => (
            <div
              key={description?.id}
              onDragOver={(e) => e?.preventDefault()}
              onDrop={() => handleDrop(description?.id)}
              onClick={() => handleDescriptionTap(description?.id)}
              className={`rounded-xl border-2 transition-all duration-200 touch-manipulation ${
                isAnswered || showFeedback ? 'cursor-not-allowed' : 'cursor-pointer active:scale-[0.99]'
              } ${getMatchStyle(description?.id)}`}
              style={{ minHeight: '72px' }}
            >
              <div className="flex items-start justify-between gap-3 px-4 py-3 sm:py-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base text-foreground leading-snug">
                    {description?.text}
                  </p>
                  {matches?.[description?.id] && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border">
                      <Icon name="User" size={14} className="text-primary flex-shrink-0" />
                      <span className="text-sm font-semibold text-primary truncate">
                        {characters?.find(c => c?.id === matches?.[description?.id])?.name}
                      </span>
                      {!showFeedback && (
                        <span className="ml-auto text-xs text-muted-foreground">tap to remove</span>
                      )}
                    </div>
                  )}
                  {!matches?.[description?.id] && selectedCharacter && (
                    <p className="text-xs text-primary/70 mt-1">Tap to assign</p>
                  )}
                </div>
                {getMatchIcon(description?.id)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submit — full-width on mobile */}
      {!showFeedback && (
        <div className="pt-2 sm:pt-4">
          <button
            onClick={handleSubmit}
            disabled={Object.keys(matches)?.length !== descriptions?.length || isAnswered}
            className={`w-full sm:w-auto sm:mx-auto sm:flex sm:justify-center py-4 sm:py-3 px-8 rounded-xl font-semibold text-base transition-all duration-200 touch-manipulation ${
              Object.keys(matches)?.length !== descriptions?.length || isAnswered
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-white hover:opacity-90 active:scale-[0.98] shadow-md'
            }`}
            style={{ minHeight: '56px' }}
          >
            <span className="flex items-center justify-center gap-2">
              Submit Matches
              <Icon name="ArrowRight" size={18} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CharacterMatchingQuestion;